import { z } from 'zod';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  paginacaoSchema,
  quantidadeSchema,
  respostaListaSchema,
  situacaoAprovacaoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Solicitação de compra (`.spec/010`, "Solicitação de Compra"; `.spec/016`,
 * seção 7).
 *
 * É o pedido interno: a obra diz do que precisa, e o setor de compras decide de
 * quem comprar. Mestre e itens viajam **num payload só**, como na condição de
 * pagamento — uma rota separada para os itens deixaria existir solicitação
 * gravada sem item nenhum no intervalo entre as duas chamadas, e é justamente
 * essa a linha que nenhum pedido atende.
 *
 * Três diferenças em relação ao lançamento de estoque, que serviu de modelo:
 *
 * - **Tem `PUT`.** Lá a gravação mexe no saldo e desfazer é impossível; aqui ela
 *   mexe só no documento. A trava equivalente é a situação: só se altera
 *   `PENDENTE`.
 * - **Não se exclui, encerra-se.** `CANCELADO` e `BAIXADO` são situações com
 *   motivo escrito, e são o que sustenta o relatório de "quanto a obra pede e
 *   não se compra".
 * - **Não tem valor.** Preço é da cotação, que aponta para o item daqui e fica
 *   para o módulo seguinte.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Situação da solicitação — **cinco valores, um a mais que o pedido**.
 *
 * Enum próprio, e não o `situacaoDocumentoSchema` compartilhado, porque
 * `COTACAO` é dela: o pedido de compra não se cota, e acrescentar o valor ao
 * enum comum o ofereceria no filtro de uma tela onde nenhuma linha jamais o
 * teria. As quatro primeiras continuam sendo as mesmas, na mesma ordem.
 *
 * `COTACAO` fica **entre pendente e atendido**, que é a ordem do processo: a
 * obra pede, o comprador manda cotar, o pedido atende. A ordem do enum é a ordem
 * do `<select>` da listagem.
 *
 * **Derivada e gravada**, como a situação do pedido: quem a escreve é
 * `recalcularSituacaoDasSolicitacoes`, chamada por toda escrita que muda o
 * atendimento ou a cotação dos itens. Calculá-la na leitura obrigaria a listagem
 * a um `EXISTS` sobre `cotacoes_preco` em cada linha da página — que foi
 * exatamente o que esta coluna substituiu.
 */
export const situacaoSolicitacaoSchema = z.enum([
  'PENDENTE',
  'COTACAO',
  'ATENDIDO',
  'CANCELADO',
  'BAIXADO',
]);

export const SITUACOES_SOLICITACAO_COMPRA = situacaoSolicitacaoSchema.options;
export type SituacaoSolicitacaoCompra = z.infer<typeof situacaoSolicitacaoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_SITUACAO_SOLICITACAO: Readonly<Record<SituacaoSolicitacaoCompra, string>> = {
  PENDENTE: 'Pendente',
  COTACAO: 'Cotação',
  ATENDIDO: 'Atendido',
  CANCELADO: 'Cancelado',
  BAIXADO: 'Baixado',
};

/**
 * O que cada situação significa, para a dica da tela.
 *
 * "Cancelado" e "baixado" são indistinguíveis pelo nome, e a diferença entre os
 * dois é o ponto do indicador: cancelada é a solicitação que não deveria ter
 * existido; baixada é a legítima cujo saldo não vai ser comprado. Somar as duas
 * no relatório esconderia quanto do que a obra pede acaba não virando compra.
 */
export const DICA_SITUACAO_SOLICITACAO: Readonly<Record<SituacaoSolicitacaoCompra, string>> = {
  PENDENTE: 'Sem atendimento e sem cotação. É a única situação que se altera.',
  COTACAO:
    'Ao menos um item já virou cotação de preço, e o comprador espera resposta do fornecedor. Situação automática.',
  ATENDIDO: 'Todos os itens foram cobertos por pedido de compra. Situação automática.',
  CANCELADO: 'Anulada sem nenhum atendimento — o documento não deveria ter existido.',
  BAIXADO: 'Encerrada com saldo em aberto que não será comprado.',
};

export const SITUACOES_APROVACAO_SOLICITACAO = situacaoAprovacaoSchema.options;
export type SituacaoAprovacaoSolicitacao = z.infer<typeof situacaoAprovacaoSchema>;

// Os rótulos da situação de aprovação moram em `aprovacao-compra.ts`: desde a
// decisão 108 eles servem aos três documentos de compra, e não só a este.

/**
 * De onde o documento veio.
 *
 * `MOBILE` é o aplicativo do canteiro, que é offline-first (`.spec/014`). Não é
 * campo do usuário: quem o preenche é a API, pela rota que recebeu a chamada.
 * Aceitá-lo no payload deixaria o escritório gravar um documento como se tivesse
 * vindo da obra, e o relatório que compara os dois perderia o sentido.
 */
export const ORIGENS_SOLICITACAO_COMPRA = ['WEB', 'MOBILE'] as const;
export const origemSolicitacaoCompraSchema = z.enum(ORIGENS_SOLICITACAO_COMPRA);
export type OrigemSolicitacaoCompra = z.infer<typeof origemSolicitacaoCompraSchema>;

export const ROTULO_ORIGEM_SOLICITACAO: Readonly<Record<OrigemSolicitacaoCompra, string>> = {
  WEB: 'Escritório',
  MOBILE: 'Aplicativo',
};

/**
 * Teto de itens por solicitação.
 *
 * Não é julgamento sobre o negócio: existe para que um `curl` com dez mil linhas
 * não vire dez mil `INSERT` numa transação. Uma solicitação real de obra grande
 * chega a algumas dezenas de itens, e 200 dá folga de uma ordem de grandeza.
 */
export const MAXIMO_ITENS_SOLICITACAO = 200;

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Quantidade solicitada: **sempre positiva**.
 *
 * Zero não pede nada e ocuparia uma linha que o comprador teria de ler para
 * descobrir que não há o que comprar. Negativo é devolução, que é outro
 * documento.
 */
const quantidadeSolicitadaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade solicitada deve ser maior que zero.',
);

/**
 * Item no payload de gravação.
 *
 * **Não tem `quantidadeAtendida`**, e a coluna existe. Quem a escreve é o pedido
 * de compra, ao consumir o saldo desta linha. Aceitá-la aqui deixaria a
 * solicitação se declarar atendida sem que nenhum pedido tivesse sido feito — e
 * o relatório de atendimento passaria a medir o que o usuário digitou, não o que
 * a empresa comprou.
 *
 * **Não tem `id`.** Os itens são regravados por inteiro a cada `PUT`, como as
 * parcelas da condição de pagamento. O identificador do item existe no banco
 * porque a cotação vai apontar para ele; enquanto não há cotação, preservá-lo
 * não muda nada e a regravação é o caminho mais simples que não erra.
 */
export const itemSolicitacaoCompraEntradaSchema = z.object({
  /**
   * O insumo, e **não** a linha de `insumos_empresa` que o banco guarda.
   *
   * A coluna aponta para `insumos_empresa` (`.spec/016`, seção 7) e continua
   * apontando: é ela que diz que aquela empresa usa aquele material. Quem
   * resolve o vínculo a partir do par empresa × insumo é o servidor.
   *
   * A alternativa — pedir o `insumoEmpresaId` ao cliente — obrigaria a tela a
   * abrir o cadastro de cada insumo para descobrir o id do vínculo, porque a
   * listagem devolve o insumo, não os vínculos dele. Seriam N consultas para
   * montar um combo, e o primeiro cliente que errasse o par gravaria material de
   * uma empresa na solicitação de outra sem que nada acusasse.
   */
  insumoId: uuidSchema,
  /** Para onde este material vai. Por item: um documento serve dois locais. */
  depositoId: uuidSchema,
  quantidadeSolicitada: quantidadeSolicitadaSchema,
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').optional(),
});

export type ItemSolicitacaoCompraEntrada = z.infer<typeof itemSolicitacaoCompraEntradaSchema>;

/**
 * O mesmo insumo para o mesmo depósito, duas vezes, é sempre digitação repetida.
 *
 * A soma das duas linhas é o que o usuário queria numa só, e mantê-las separadas
 * faria o pedido consumir uma e deixar a outra pendente para sempre. O
 * `UNIQUE (tenant_id, solicitacao_compra_id, insumo_empresa_id, deposito_id)`
 * recusa no banco; aqui a recusa chega presa à linha certa, que é o que permite
 * ao formulário destacá-la.
 *
 * Insumo repetido em **depósitos diferentes** é legítimo e passa: pedir vinte
 * sacos para o canteiro e cinco para o almoxarifado é um pedido só.
 */
function validarItensDistintos(itens: ItemSolicitacaoCompraEntrada[], ctx: z.RefinementCtx): void {
  const vistos = new Set<string>();

  itens.forEach((item, indice) => {
    const chave = `${item.insumoId}·${item.depositoId}`;
    if (!vistos.has(chave)) {
      vistos.add(chave);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'insumoId'],
      message: 'Este insumo já foi pedido para este depósito. Some as quantidades numa linha só.',
    });
  });
}

const itensSchema = z
  .array(itemSolicitacaoCompraEntradaSchema)
  .min(1, 'A solicitação precisa de pelo menos um item.')
  .max(MAXIMO_ITENS_SOLICITACAO, `A solicitação tem no máximo ${MAXIMO_ITENS_SOLICITACAO} itens.`)
  .superRefine(validarItensDistintos);

/** Item como a API o devolve, com os nomes já resolvidos e o saldo em aberto. */
export const itemSolicitacaoCompraSchema = z.object({
  id: uuidSchema,
  /** O vínculo gravado. Vem na leitura porque a cotação vai apontar para ele. */
  insumoEmpresaId: uuidSchema,
  /** O insumo por trás do vínculo — é ele que o formulário devolve no `PUT`. */
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  unidadeSigla: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  /** Strings decimais, nunca number: ponto flutuante perde casas em silêncio. */
  quantidadeSolicitada: quantidadeSchema,
  quantidadeAtendida: quantidadeSchema,
  /**
   * O que os pedidos compraram **acima** do que a obra pediu.
   *
   * É a outra metade da atendida, que tem teto no solicitado por regra de
   * `.spec/010`. O que o teto corta parava aqui: a política "Alertar e permitir"
   * da conta prometia que "o excedente fica registrado", e nada o registrava.
   *
   * Zero é o caso normal. Valor aqui significa que alguém comprou a mais **e a
   * conta permitiu** — pela política `ALERTAR`, ou pela `TOLERANCIA` dentro do
   * percentual. Com `BLOQUEAR` a gravação teria sido recusada, e a coluna fica
   * em zero para sempre.
   *
   * Não confundir com `quantidadeBaixada`, logo abaixo, que é o oposto exato: o
   * saldo que **faltou** e não vai ser comprado.
   */
  quantidadeExcedente: quantidadeSchema,
  /**
   * O saldo que **não vai ser comprado** (`.spec/010`).
   *
   * Escrita pelo encerramento, e só quando o destino é `BAIXADO`: é o que
   * registra "a obra pediu 100, o pedido cobriu 60, os 40 restantes não serão
   * comprados". Nunca é campo do usuário — ela cai por consequência da decisão
   * de baixar, e digitá-la à parte deixaria baixar 40 de um saldo de 10.
   *
   * Ela existe como coluna, e não como conta na leitura, porque `solicitada −
   * atendida` de uma solicitação baixada continuaria mostrando saldo em aberto
   * para sempre: a baixa é justamente o fato que diz que aquele saldo acabou.
   * Em `CANCELADO` continua zero — cancelar é dizer que o documento não deveria
   * ter existido, e nada dele foi comprado nem deixou de ser.
   */
  quantidadeBaixada: quantidadeSchema,
  /**
   * `solicitada − atendida − baixada`, calculado pelo servidor.
   *
   * Vem pronto porque é o número que a tela mostra e o que o comprador usa para
   * montar o pedido. Calculá-lo no cliente daria duas implementações do mesmo
   * subtrair de string decimal, e a do cliente usaria `Number` em algum momento.
   *
   * A baixada entra na conta: depois de baixado, o saldo em aberto é zero — é
   * exatamente isso que baixar significa, e deixá-lo aparecendo faria a
   * solicitação encerrada continuar chamando o comprador para comprar.
   */
  quantidadeEmAberto: quantidadeSchema,
  /**
   * Este item já deu origem a uma cotação de preço?
   *
   * Vem do servidor porque é o que **trava a alteração do documento**: a cotação
   * aponta para esta linha, e regravar os itens — que é como o `PUT` funciona,
   * apagando e reinserindo — deixaria a cotação apontando para um item que não
   * existe mais. O banco recusa por chave estrangeira; o campo existe para que a
   * tela saiba disso **antes** de o usuário preencher a solicitação inteira.
   *
   * Por item, e não no cabeçalho, porque é o item que a cotação referencia — e
   * porque a tela marca a linha cotada, que é a informação útil quando só uma de
   * quinze está travando o documento.
   */
  cotado: z.boolean(),
  observacao: z.string().nullable(),
});

export type ItemSolicitacaoCompra = z.infer<typeof itemSolicitacaoCompraSchema>;

/**
 * Qual linha está travando o documento?
 *
 * **Deixou de ser a trava** quando `COTACAO` virou situação: a solicitação com
 * item cotado não é mais `PENDENTE`, e `solicitacaoEditavel` já a recusa pela
 * situação — que é o teste que a API faz primeiro. O que sobrou aqui é a
 * pergunta da *tela*: quais linhas marcar, e qual das duas explicações dar a
 * quem encontra o formulário travado (cotação aberta ou pedido já emitido).
 *
 * Mantida também como rede: o `cotado` do item e a coluna `situacao` são
 * escritos em momentos diferentes, e uma divergência entre os dois aparece aqui
 * como formulário travado — que é falha fechada, e não edição indevida.
 */
export function solicitacaoComCotacao(itens: readonly { cotado: boolean }[]): boolean {
  return itens.some((item) => item.cotado);
}

/* -------------------------------------------------------------------------- */
/* Itens em aberto — o que a cotação avulsa pode vincular                     */
/* -------------------------------------------------------------------------- */

/**
 * Os itens que ainda esperam compra, de um material e de uma empresa.
 *
 * Existe para **um** caso: a cotação aberta por fora — o comprador que ligou
 * para o fornecedor antes de olhar as solicitações — e que depois descobre que
 * havia um pedido da obra para aquele mesmo material. Sem esta lista, ligar as
 * duas coisas exigiria abrir a tela de solicitações, achar o documento, copiar o
 * id do item e colá-lo em algum lugar que não existe.
 *
 * A empresa e o insumo são **obrigatórios**, e não filtros opcionais: a lista só
 * faz sentido para o par que a cotação já escolheu, e sem eles a rota devolveria
 * todos os itens em aberto do tenant — que é uma consulta de relatório, não de
 * seletor. É também o que faz "mesma empresa, mesmo insumo" ser uma propriedade
 * da consulta em vez de uma checagem que alguém pode esquecer.
 */
export const listarItensEmAbertoQuerySchema = paginacaoSchema.extend({
  /** A empresa da cotação. O item pertence a ela pelo vínculo `insumos_empresa`. */
  empresaId: uuidSchema,
  /** O insumo cotado. É o mesmo par empresa × insumo que a cotação resolve. */
  insumoId: uuidSchema,
});

export type ListarItensEmAbertoQuery = z.infer<typeof listarItensEmAbertoQuerySchema>;

/**
 * O item como o seletor de vínculo o mostra.
 *
 * É um recorte do item completo, e não ele inteiro: quem escolhe precisa saber
 * **de que documento**, **de que obra** e **quanto falta** — o resto (insumo,
 * observação, atendido, baixado) ou é igual em todas as linhas, porque o insumo
 * é o mesmo, ou não ajuda a decidir.
 *
 * A solicitação vem resolvida pelo mesmo motivo do atendimento da cotação: o id
 * do item não é endereço de tela nenhuma, e é o documento que se abre.
 */
export const itemSolicitacaoEmAbertoSchema = z.object({
  /** O **item**. É ele que a cotação grava como procedência. */
  id: uuidSchema,
  solicitacaoCompraId: uuidSchema,
  dataLancamento: dataSchema,
  obraNome: z.string().nullable(),
  depositoNome: z.string().nullable(),
  quantidadeSolicitada: quantidadeSchema,
  /** `solicitada − atendida − baixada`. É sempre maior que zero nesta lista. */
  quantidadeEmAberto: quantidadeSchema,
  unidadeSigla: z.string().nullable(),
});

export type ItemSolicitacaoEmAberto = z.infer<typeof itemSolicitacaoEmAbertoSchema>;

export const listaItensEmAbertoSchema = respostaListaSchema(itemSolicitacaoEmAbertoSchema);
export type ListaItensEmAberto = z.infer<typeof listaItensEmAbertoSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Não tem `situacao`, `origem` nem os campos de aprovação.** As três ausências
 * são a mesma decisão: o servidor é quem sabe.
 *
 * - `situacao` é derivada do atendimento dos itens (`.spec/001`, seção 2.2 (e)).
 *   Aceitá-la deixaria marcar como atendida uma solicitação que nenhum pedido
 *   tocou.
 * - `origem` vem da rota que recebeu a chamada, não do corpo dela.
 * - `situacaoAprovacao` é do fluxo de autorização, e muda só pelos gestos
 *   dele — enviar, aprovar, reprovar, reabrir (`aprovacao-compra.ts`, decisão
 *   108).
 *
 * `motivoEncerramento` também fica de fora, e por outro motivo: encerrar é uma
 * ação própria, com rota própria, porque exige verificar o atendimento dos itens
 * antes de decidir se o caso é cancelar ou baixar.
 */
export const criarSolicitacaoCompraSchema = z.object({
  /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona dos depósitos. */
  empresaId: uuidSchema,
  /** Dia da solicitação. Data de negócio, sem hora. */
  dataLancamento: dataDoFatoSchema,
  /** Obra que vai consumir o material. Ausente na compra de escritório. */
  obraId: uuidSchema.nullable().prefault(null),
  /** Etapa da obra. Só pode vir com a obra — a API recusa a parte sozinha. */
  obraParteId: uuidSchema.nullable().prefault(null),
  /** Depósito **sugerido** para os itens. Quem manda é o depósito de cada item. */
  depositoId: uuidSchema,
  itens: itensSchema,
  observacao: z.string().trim().max(1000, 'A observação tem no máximo 1000 caracteres.').optional(),
});

export type CriarSolicitacaoCompra = z.infer<typeof criarSolicitacaoCompraSchema>;
export const atualizarSolicitacaoCompraSchema = criarSolicitacaoCompraSchema;
export type AtualizarSolicitacaoCompra = z.infer<typeof atualizarSolicitacaoCompraSchema>;

/**
 * Solicitação como a API a devolve.
 *
 * Os nomes de empresa, obra e depósito vêm **desnormalizados**: a listagem mostra
 * "Residencial Aurora · Estoque Geral" e não dois uuid, e resolver isso no
 * cliente significaria duas consultas por linha.
 */
export const solicitacaoCompraSchema = z.object({
  id: uuidSchema,
  /**
   * O número sequencial do documento, **do servidor**.
   *
   * Não está no payload de gravação e nunca estará: quem o atribui é o contador
   * da conta, na transação que cria a solicitação. Aceitá-lo do cliente deixaria
   * dois documentos nascerem com o mesmo número — e o segundo só descobriria
   * isso no índice único, como erro de banco.
   *
   * Único na **conta inteira**, independentemente da empresa do grupo: é o
   * número que a obra diz ao telefone, e ele não deve exigir dizer também de
   * qual empresa se fala.
   */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataLancamento: dataSchema,
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  situacao: situacaoSolicitacaoSchema,
  motivoEncerramento: z.string().nullable(),
  /**
   * Da autorização (decisão 108). Quem aprovou, quando e o motivo da
   * reprovação estão no histórico (`GET …/:id/aprovacao`), e não em colunas do
   * documento: o ciclo pode passar por várias reprovações, e uma coluna só
   * guardaria a última.
   */
  situacaoAprovacao: situacaoAprovacaoSchema,
  origem: origemSolicitacaoCompraSchema,
  observacao: z.string().nullable(),
  /**
   * Os itens vêm **na listagem também**, não só no documento aberto.
   *
   * É o que distingue duas solicitações da mesma obra no mesmo dia, e é o que a
   * coluna "Itens" resume. O custo é uma segunda consulta por página, não uma
   * por linha — a mesma escolha das parcelas da condição de pagamento.
   */
  itens: z.array(itemSolicitacaoCompraSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type SolicitacaoCompra = z.infer<typeof solicitacaoCompraSchema>;

export const listaSolicitacoesCompraSchema = respostaListaSchema(solicitacaoCompraSchema);
export type ListaSolicitacoesCompra = z.infer<typeof listaSolicitacoesCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Encerramento                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Cancelar ou baixar, sempre com motivo.
 *
 * As duas são a mesma operação com destinos distintos, e por isso uma rota só:
 * qual delas é permitida depende do atendimento dos itens, que é a verificação
 * que o servidor faz nos dois casos. Duas rotas repetiriam a verificação e
 * divergiriam na primeira correção.
 *
 * O motivo é obrigatório e não aceita espaço em branco — `.trim().min(5)`
 * recusa o ponto solitário que alguém digita para passar da validação, e cinco
 * caracteres é o mínimo em que cabe "quebra" ou "erro".
 */
export const encerrarSolicitacaoCompraSchema = z.object({
  destino: z.enum(['CANCELADO', 'BAIXADO']),
  motivo: z
    .string()
    .trim()
    .min(5, 'Escreva o motivo do encerramento.')
    .max(500, 'O motivo tem no máximo 500 caracteres.'),
});

export type EncerrarSolicitacaoCompra = z.infer<typeof encerrarSolicitacaoCompraSchema>;

/* -------------------------------------------------------------------------- */
/* Geração de cotações                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Abrir as cotações desta solicitação, de uma vez.
 *
 * **Uma cotação por item**, e não uma cotação com a lista toda. A regra é da
 * cotação, não uma escolha desta rota: cotar cimento e cabo é cotar duas coisas
 * — os fornecedores são outros, o prazo é outro, e a proposta de quem cobre um
 * item e não o outro não se compara com nada (ver `cotacoes-preco.ts`). Uma
 * cotação com N insumos obrigaria a inventar como comparar propostas parciais.
 *
 * A quantidade cotada é o **saldo em aberto** do item, e não a solicitada: o que
 * já foi coberto por pedido não se cota de novo, e cotar o total faria o
 * comprador negociar por uma quantidade que ele não vai comprar.
 *
 * Os itens sem saldo em aberto ficam de fora, e a resposta diz quantos foram
 * pulados. A rota **não falha** quando todos estão cobertos: a solicitação
 * inteiramente atendida é um estado legítimo, e um erro ali se leria como
 * defeito.
 *
 * O item que **já tem cotação** também fica de fora, e pela mesma lógica:
 * mandar gerar duas vezes é o que qualquer um faz quando não tem certeza se a
 * primeira funcionou, e a segunda chamada não pode abrir cotações repetidas do
 * mesmo saldo. A resposta conta esses itens à parte dos sem saldo, porque as
 * duas causas pedem frases diferentes na tela.
 */
export const gerarCotacoesDaSolicitacaoSchema = z.object({
  /**
   * Dia das cotações geradas. Ausente vale **hoje**, resolvido no servidor.
   *
   * Não é a data da solicitação: a solicitação é de abril, e o preço que se está
   * perguntando é o de hoje. Copiar a data do documento de origem produziria
   * cotação nascida vencida.
   */
  dataCotacao: dataSchema.optional(),
  /** Até quando se espera resposta dos fornecedores, igual em todas. Opcional. */
  prazoResposta: dataSchema.nullable().prefault(null),
  /**
   * Quais itens cotar. Vazio ou ausente significa **todos os que têm saldo**.
   *
   * Existe para a tela que deixa marcar as linhas, e o padrão é o caminho curto:
   * o comprador que abriu a solicitação e clicou em "gerar cotações" quase
   * sempre quer todas.
   */
  itemIds: z.array(uuidSchema).max(MAXIMO_ITENS_SOLICITACAO).optional(),
});

export type GerarCotacoesDaSolicitacao = z.infer<typeof gerarCotacoesDaSolicitacaoSchema>;

/** O que a geração fez, para a tela dizer ao usuário sem reler nada. */
export const cotacoesGeradasSchema = z.object({
  /** Ids das cotações criadas, na ordem dos itens. */
  cotacaoIds: z.array(uuidSchema),
  /**
   * Itens **pedidos** que não tinham saldo, e por isso não viraram cotação.
   *
   * Conta só o que entrou na seleção — ou todos, quando `itemIds` vem vazio. O
   * que o usuário desmarcou de propósito fica de fora desta conta: somá-lo aqui
   * faria a tela dizer "ficou de fora por não ter saldo" sobre uma linha que ele
   * mesmo tirou, explicando a decisão dele com o motivo errado.
   *
   * Com a tela de seleção, o número normal é **zero**: ele só cresce quando um
   * pedido cobriu o saldo entre a abertura do diálogo e a confirmação.
   */
  itensSemSaldo: z.number().int(),
  /**
   * Itens **pedidos** que já tinham cotação, e por isso não viraram outra.
   *
   * Separado de `itensSemSaldo` porque a causa é outra e o que o comprador faz a
   * seguir é outro: sem saldo, não há o que cotar; já cotado, a cotação existe e
   * é para ela que ele deve ir. Somar os dois num número só faria a tela dizer
   * "ficou de fora" sem explicar o que fazer.
   *
   * O número normal é **zero**. Ele cresce quando alguém manda gerar duas vezes
   * — o que é a reação natural de quem não viu a primeira confirmação.
   */
  itensJaCotados: z.number().int(),
});

export type CotacoesGeradas = z.infer<typeof cotacoesGeradasSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: documento não se inativa, e a coluna equivalente é `situacao`.
 * O recorte natural é **situação e período** — "o que está pendente" é a
 * pergunta que a tela abre respondendo, e é exatamente o índice
 * `(tenant_id, situacao)` que `.spec/004`, seção 2, pede para os documentos de
 * compra.
 */
export const listarSolicitacoesCompraQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  /**
   * O número do documento, exato.
   *
   * É a busca que substituiu o `q` removido daqui: quem procura uma solicitação
   * a procura pelo número, que é o que está escrito no papel em cima da mesa.
   * Exato porque o número é curto — "43" como "contém" traria 43, 143, 430 e
   * 4300, e a lista deixaria de responder a pergunta.
   */
  numero: z.coerce.number().int().min(1).optional(),
  empresaId: uuidSchema.optional(),
  /**
   * O material pedido, por id.
   *
   * Alcança os **itens**, e não o cabeçalho: a solicitação não tem insumo, cada
   * linha dela tem. É `EXISTS` no servidor, não junção — a junção traria a
   * solicitação repetida uma vez por item e a paginação passaria a contar itens.
   *
   * Substituiu a busca livre, que varria observação e nome da obra. As duas
   * metades envelheceram mal: a obra já tem filtro próprio ao lado, e a
   * observação é anotação de quem digitou — procurar por ela devolvia acertos
   * que ninguém pediu.
   */
  insumoId: uuidSchema.optional(),
  obraId: uuidSchema.optional(),
  depositoId: uuidSchema.optional(),
  /**
   * Igualdade simples sobre a coluna, e é o que faz "o que está em cotação" ser
   * uma leitura do índice `(tenant_id, situacao)` em vez de um `EXISTS` por
   * linha da página. É a razão de `COTACAO` ser situação gravada.
   */
  situacao: situacaoSolicitacaoSchema.optional(),
  /**
   * A fila do autorizador: `AGUARDANDO` traz o que espera decisão (decisão
   * 108). Igualdade sobre a coluna, como a situação.
   */
  situacaoAprovacao: situacaoAprovacaoSchema.optional(),
  origem: origemSolicitacaoCompraSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarSolicitacoesCompraQuery = z.infer<typeof listarSolicitacoesCompraQuerySchema>;

export const COLUNAS_ORDENAVEIS_SOLICITACAO_COMPRA = [
  'numero',
  'dataLancamento',
  'situacao',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * A solicitação ainda se altera?
 *
 * Vive no contrato, e não no formulário nem no service, porque os dois precisam
 * da **mesma** regra: a tela decide se os campos são editáveis, e a API decide
 * se aceita o `PUT`. Duas cópias divergiriam na primeira correção, e a
 * divergência apareceria como formulário que deixa digitar e servidor que
 * recusa ao salvar.
 *
 * `ATENDIDO` não se altera junto com os encerrados de propósito: mexer nos itens
 * de uma solicitação já coberta por pedido mudaria o saldo que o pedido
 * consumiu.
 *
 * `COTACAO` também não, e passou a ser recusada **por esta linha**: antes a
 * trava vinha de `solicitacaoComCotacao`, sobre os itens, porque a solicitação
 * cotada continuava `PENDENTE`. Com a situação gravada, a mesma regra cabe na
 * comparação que já existia — regravar os itens deixaria a cotação apontando
 * para uma linha que mudou de quantidade debaixo dela.
 */
export function solicitacaoEditavel(situacao: SituacaoSolicitacaoCompra): boolean {
  return situacao === 'PENDENTE';
}

/**
 * Qual encerramento cabe, dado o atendimento dos itens.
 *
 * Cancelar exige atendimento zero em **todos** os itens; com atendimento
 * parcial, o caso é baixar. A regra é de `.spec/001`, seção 2.2 (e), e está
 * implementada em `validarEncerramento`, em `@construtora/domain`. Esta função
 * não a duplica: ela responde a pergunta que a *tela* faz — qual botão oferecer
 * — sobre os dados que a tela já tem.
 */
export function encerramentoPermitido(
  itens: readonly { quantidadeAtendida: string }[],
): 'CANCELADO' | 'BAIXADO' {
  const temAtendimento = itens.some((item) => Number(item.quantidadeAtendida) > 0);
  return temAtendimento ? 'BAIXADO' : 'CANCELADO';
}

/**
 * Resumo dos itens: `3 itens · 2 em aberto`.
 *
 * **Nenhuma tela usa hoje.** A listagem de solicitações trocou o resumo pela
 * lista dos materiais com as quantidades — o resumo dizia *quantos*, e a
 * pergunta de quem varre a tela é *o quê*. Fica exportada porque a conta de "em
 * aberto" continua sendo uma só, e a próxima tela que precisar do resumo (o
 * cartão da versão móvel, por exemplo) deve usá-la em vez de recontar: duas
 * implementações escreveriam "0 em aberto" e "todos atendidos" para o mesmo
 * documento.
 */
export function resumoDeItens(itens: readonly ItemSolicitacaoCompra[]): string {
  if (itens.length === 0) return '—';

  const plural = itens.length === 1 ? 'item' : 'itens';
  const emAberto = itens.filter((item) => Number(item.quantidadeEmAberto) > 0).length;

  if (emAberto === 0) return `${itens.length} ${plural} · tudo atendido`;
  return `${itens.length} ${plural} · ${emAberto} em aberto`;
}
